import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { fr, enUS, ar } from 'date-fns/locale';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import 'moment/locale/fr';
import 'moment/locale/en-gb';

// Enregistrement des locales pour react-datepicker
registerLocale('fr', fr);
registerLocale('en', enUS);
registerLocale('ar', ar);

// Configuration des langues supportées
export const SUPPORTED_LANGUAGES = {
  fr: { name: 'Français', locale: fr },
  ar: { name: 'العربية', locale: ar },
  en: { name: 'English', locale: enUS },
};

const LanguageContext = createContext();

const translations = {
  fr: {
    // Navigation
    profile: 'Profil',
    preferences: 'Préférences',
    settings: 'Paramètres',
    languagePreferences: 'Préférences de langue',
    language: 'Langue',
    
    // Boutons
    save: 'Enregistrer',
    saveChanges: 'Enregistrer les modifications',
    editProfile: 'Modifier le profil',
    cancel: 'Annuler',
    edit: 'Modifier',
    delete: 'Supprimer',
    change: 'Changer',
    enable: 'Activer',
    
    // Messages
    saveSuccess: 'Modifications enregistrées avec succès',
    languageChangeNote: 'Le changement de langue nécessite un rechargement de la page.',
    contactSupport: 'Contactez le support pour modifier votre adresse email',
    
    // Champs du profil
    personalInfo: 'Informations personnelles',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'Email',
    phone: 'Téléphone',
    address: 'Adresse',
    
    // Sécurité
    security: 'Sécurité',
    password: 'Mot de passe',
    updatePassword: 'Mettez à jour votre mot de passe régulièrement',
    twoFactorAuth: 'Authentification à deux facteurs',
    addSecurity: 'Ajoutez une couche de sécurité supplémentaire à votre compte',
    
    // Préférences
    notifications: 'Notifications',
    enableNotifications: 'Activer les notifications',
    receiveUpdates: 'Recevoir des mises à jour par email',
    newsletter: 'Newsletter',
    receiveOffers: 'Recevoir nos offres spéciales et actualités',
    darkMode: 'Mode sombre',
    enableDarkMode: 'Activer le mode sombre pour un meilleur confort visuel',
    
    // Messages d'erreur
    errorSaving: 'Erreur lors de la sauvegarde',
    errorLoading: 'Erreur lors du chargement des données',
    
    // Danger zone
    dangerZone: 'Zone dangereuse',
    deleteAccount: 'Supprimer le compte',
    deleteWarning: 'Cette action est irréversible. Toutes vos données seront définitivement supprimées.'
  },
  en: {
    // Navigation
    profile: 'Profile',
    preferences: 'Preferences',
    settings: 'Settings',
    languagePreferences: 'Language Preferences',
    language: 'Language',
    
    // Buttons
    save: 'Save',
    saveChanges: 'Save changes',
    editProfile: 'Edit profile',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    change: 'Change',
    enable: 'Enable',
    
    // Messages
    saveSuccess: 'Changes saved successfully',
    languageChangeNote: 'Changing language requires page reload.',
    contactSupport: 'Contact support to change your email address',
    
    // Profile fields
    personalInfo: 'Personal Information',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    
    // Security
    security: 'Security',
    password: 'Password',
    updatePassword: 'Update your password regularly',
    twoFactorAuth: 'Two-Factor Authentication',
    addSecurity: 'Add an extra layer of security to your account',
    
    // Preferences
    notifications: 'Notifications',
    enableNotifications: 'Enable notifications',
    receiveUpdates: 'Receive email updates',
    newsletter: 'Newsletter',
    receiveOffers: 'Receive our special offers and news',
    darkMode: 'Dark Mode',
    enableDarkMode: 'Enable dark mode for better visual comfort',
    
    // Error messages
    errorSaving: 'Error saving changes',
    errorLoading: 'Error loading data',
    
    // Danger zone
    dangerZone: 'Danger Zone',
    deleteAccount: 'Delete Account',
    deleteWarning: 'This action cannot be undone. All your data will be permanently deleted.'
  }
};

// Détecter la langue du navigateur
const getBrowserLanguage = () => {
  if (typeof window === 'undefined') return 'fr';
  
  const browserLang = navigator.language || navigator.userLanguage;
  // Extraire le code de langue (par exemple 'fr-FR' -> 'fr')
  const langCode = browserLang.split('-')[0];
  
  // Vérifier si la langue détectée est supportée, sinon utiliser le français
  return SUPPORTED_LANGUAGES[langCode] ? langCode : 'fr';
};

export const LanguageProvider = ({ children }) => {
  // Récupérer la langue depuis le localStorage ou détecter celle du navigateur
  const getInitialLanguage = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('userLanguage') || getBrowserLanguage();
    }
    return 'fr';
  };

  const [language, setLanguage] = useState(getInitialLanguage());
  const [dateLocale, setDateLocale] = useState(SUPPORTED_LANGUAGES[getInitialLanguage()]?.locale || fr);
  
  // Mémoize les traductions pour éviter les re-rendus inutiles
  // Si une traduction est manquante, on utilise la traduction française par défaut
  const t = useMemo(() => {
    const defaultTranslations = translations.fr;
    const currentTranslations = translations[language] || {};
    
    // Fusionner avec les traductions par défaut pour s'assurer qu'aucune clé ne manque
    return {
      ...defaultTranslations,
      ...currentTranslations
    };
  }, [language]);

  // Effet pour initialiser la langue au chargement
  useEffect(() => {
    const savedLanguage = getInitialLanguage();
    if (savedLanguage && SUPPORTED_LANGUAGES[savedLanguage]) {
      setLanguage(savedLanguage);
      setDateLocale(SUPPORTED_LANGUAGES[savedLanguage].locale);
      setDefaultLocale(savedLanguage);
      document.documentElement.lang = savedLanguage;
    }
  }, []);

  const changeLanguage = (lang) => {
    if (language !== lang && SUPPORTED_LANGUAGES[lang]) {
      // Mettre à jour le state
      setLanguage(lang);
      setDateLocale(SUPPORTED_LANGUAGES[lang].locale);
      
      // Sauvegarder dans le localStorage
      localStorage.setItem('userLanguage', lang);
      
      // Mettre à jour le document et les paramètres de langue
      document.documentElement.lang = lang;
      setDefaultLocale(lang);
      
      // Mettre à jour la langue du navigateur
      if (window.navigator && window.navigator.language !== lang) {
        // Utiliser l'API de préférences de langue du navigateur si disponible
        if (navigator.languages) {
          try {
            navigator.languages = [lang];
          } catch (e) {
            console.warn('Cannot modify navigator.languages', e);
          }
        }
        
        // Mettre à jour le document.cookie pour le serveur
        document.cookie = `preferred_language=${lang};path=/;max-age=31536000`; // 1 an
      }
      
      // Retourner true pour indiquer que le changement a été effectué
      return true;
    }
    return false;
  };

  // Valeur du contexte
  const contextValue = useMemo(() => ({
    language,
    t,
    dateLocale,
    changeLanguage,
    languages: Object.entries(SUPPORTED_LANGUAGES).map(([code, { name }]) => ({
      code,
      name
    })),
    isRTL: language === 'ar',
    dir: language === 'ar' ? 'rtl' : 'ltr'
  }), [language, t, dateLocale]);

  return (
    <LanguageContext.Provider value={contextValue}>
      <div dir={contextValue.dir}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
